/**
 * What the Markets tab's order book shows, worked out once.
 *
 * The panel needs three things the simulation knows and does not hand over in
 * one piece: which crowns the player may actually trade with (and *why* not,
 * where the answer is "not yet"), the book filtered down to the orders those
 * crowns have posted, and the same sort `matchMarket` uses — because "the
 * cheapest ask" on screen and "the cheapest ask" in the matcher must be the
 * same line, or the panel is telling the player a story about a market that
 * does not exist.
 *
 * It lives here rather than in `worldmap.ts` for the reason `income.ts` does:
 * a test can call it. And it is written to be called **once per render of the
 * section, not once per row** — `bookView` does exactly one breadth-first walk
 * (`seatHops`), and everything after it is a lookup.
 *
 * Nothing here derives a rule of its own. Reach is `nationReach`, the pair rule
 * is the one `matchMarket` applies, and the sort is the matcher's. What this
 * file adds is the *shape* the panel wants and the arithmetic for one question
 * the simulation has no reason to ask: how many more hulls would open a crown
 * that is currently too far away.
 */

import { fleetUpkeep } from '../sim/campaign/fleets.ts';
import { marketSeat, nationReach, seatHops } from '../sim/campaign/market.ts';
import { atWar } from '../sim/campaign/state.ts';
import {
  BASE_REACH_HOPS,
  HULLS_PER_REACH,
  OrderKind,
  type MarketOrder,
} from '../sim/campaign/markettypes.ts';
import type { CampaignState } from '../sim/campaign/types.ts';
import type { ResourceKey } from '../sim/types.ts';

/** Where one other crown stands in relation to your merchants. */
export interface PartnerView {
  nation: number;
  /** Hops between the two seats on the combined road+lane graph, or −1. */
  hops: number;
  /** Whether the two of you may trade at all, by the matcher's own rule. */
  may: boolean;
  /** At war, which forbids the pair outright however close they are. */
  war: boolean;
  /**
   * Hulls you would have to add before your own reach covered them, or 0 when
   * the question does not arise — you already trade, you are at war, there is
   * no path at all, or their navy is what closes the gap rather than yours.
   */
  hullsWanted: number;
}

/** The book, from one crown's side of it. Built once per render. */
export interface BookView {
  /** Whose view this is. */
  me: number;
  /** Hops your merchants cover: `BASE_REACH_HOPS` plus the hull bonus. */
  reach: number;
  /** Hulls afloat, which is what buys the bonus. */
  hulls: number;
  /** One entry per nation index, so a row can look its crown up in O(1). */
  partners: PartnerView[];
  /** Living crowns other than you, and how many of them you may trade with. */
  crowns: number;
  open: number;
  /**
   * The nearest crown that is at peace with you, on the graph, and out of
   * reach — the whole "build a navy to trade further" loop, as one fact.
   */
  nearestClosed?: { nation: number; hops: number; hulls: number };
}

export function bookView(c: CampaignState, me: number): BookView {
  const hops = seatHops(c, me);
  const reach = nationReach(c, me);
  const hulls = fleetUpkeep(c, me);
  const partners: PartnerView[] = [];
  let crowns = 0;
  let open = 0;
  let nearestClosed: BookView['nearestClosed'];

  for (let i = 0; i < c.nations.length; i++) {
    const n = c.nations[i];
    if (i === me || !n?.alive) {
      partners.push({ nation: i, hops: -1, may: false, war: false, hullsWanted: 0 });
      continue;
    }
    crowns++;
    const d = hops[marketSeat(c, i)] ?? -1;
    const war = atWar(c, me, i);
    // Either side's reach carries the goods — a big navy trades both ways.
    const may = !war && d >= 0 && (d <= reach || d <= nationReach(c, i));
    if (may) open++;
    // `reach = BASE + floor(hulls / HULLS_PER_REACH)`, so covering `d` from our
    // own decks wants `(d - BASE) * HULLS_PER_REACH` hulls in total.
    const wanted =
      may || war || d < 0 ? 0 : Math.max(0, (d - BASE_REACH_HOPS) * HULLS_PER_REACH - hulls);
    partners.push({ nation: i, hops: d, may, war, hullsWanted: wanted });
    if (wanted > 0 && (!nearestClosed || d < nearestClosed.hops)) {
      nearestClosed = { nation: i, hops: d, hulls: wanted };
    }
  }
  return { me, reach, hulls, partners, crowns, open, nearestClosed };
}

/** Whether an order is one this crown could actually deal with, or its own. */
export function canDeal(view: BookView, o: MarketOrder): boolean {
  return o.nation === view.me || (view.partners[o.nation]?.may ?? false);
}

/** One good's two sides, each in the order the matcher walks them. */
export interface BookSide {
  key: ResourceKey;
  /** Asks, cheapest first — the end a buyer is served from. */
  asks: MarketOrder[];
  /** Bids, highest first. */
  bids: MarketOrder[];
  /** Orders on this good you cannot deal with, and why not. */
  beyondReach: number;
  beyondWar: number;
}

/**
 * The visible book for one good.
 *
 * The sorts are `matchMarket`'s, tie-break included: asks price-ascending then
 * id-ascending, bids price-descending then id-ascending. A panel that sorted
 * these differently would name a different order as the one about to fill.
 */
export function bookFor(c: CampaignState, view: BookView, key: ResourceKey): BookSide {
  const side: BookSide = { key, asks: [], bids: [], beyondReach: 0, beyondWar: 0 };
  for (const o of c.market.orders) {
    if (o.key !== key || o.amount <= 0) continue;
    if (!canDeal(view, o)) {
      if (view.partners[o.nation]?.war) side.beyondWar++;
      else side.beyondReach++;
      continue;
    }
    if (o.kind === OrderKind.Sell) side.asks.push(o);
    else side.bids.push(o);
  }
  side.asks.sort((x, y) => x.price - y.price || x.id - y.id);
  side.bids.sort((x, y) => y.price - x.price || x.id - y.id);
  return side;
}

/**
 * What a good is going for, in tenths — the cheapest ask and the highest bid
 * you could actually deal at, or 0 for a side nobody is standing on.
 *
 * This is what the posting form seeds a price from, so the number the player
 * starts with is one the book would actually fill against rather than a
 * table's idea of what a thing is worth.
 */
export function marketPrice(side: BookSide): { ask: number; bid: number } {
  return { ask: side.asks[0]?.price ?? 0, bid: side.bids[0]?.price ?? 0 };
}
